<template>
    <footer
        id="footer"
        data-testid="footer-guest"
        class="footer bg-surface text-content-primary py-9 wrapper"
    >
        <div class="container">
            <div class="flex flex-col flex-wrap justify-between md:flex-row">
                <div class="flex justify-between order-1 w-full mb-8 md:max-w-lg lg:max-w-sm md:mb-0">
                    <div class="w-1/2">
                        <h4 class="mb-2 lg:mb-5 font-body-1-medium">
                            {{ $t('footer.students.headline') }}
                        </h4>
                        <ul>
                            <li>
                                <a :href="route('search')">
                                    {{ $t('footer.students.search_document') }}
                                </a>
                            </li>
                            <li>
                                <a :href="route('reward.list')">
                                    {{ $t('footer.students.rewards_shop') }}
                                </a>
                            </li>
                            <li>
                                <a
                                    :href="
                                        route('landing-page.show', {
                                            slug: $t('communityGuidelinesPageSlug'),
                                        })
                                    "
                                    target="_blank"
                                >
                                    {{ $t('communityGuidelines') }}
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="w-1/2">
                        <h4 class="mb-2 lg:mb-5 font-body-1-medium">
                            {{ $t('footer.companies.headline') }}
                        </h4>
                        <ul>
                            <li>
                                <a
                                    href="https://business.studydrive.net/usecases/stellen-besetzen/"
                                    target="_blank"
                                >
                                    {{ $t('footer.companies.find_talents') }}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://business.studydrive.net/usecases/employerbranding/"
                                    target="_blank"
                                >
                                    {{ $t('footer.companies.employer_branding') }}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://business.studydrive.net/"
                                    target="_blank"
                                >
                                    {{ $t('footer.companies.demo') }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <div class="flex order-3 social-media-item md:flex-col lg:order-5">
                    <a
                        class="w-6 h-6 mr-4 md:mr-0 md:mb-3"
                        target="_blank"
                        :href="route('external.social_media.tiktok')"
                        @click.capture="pushGtmEvent('gtm_click_out_socials')"
                    >
                        <img
                            src="/images/icons/social/icon-tiktok.svg"
                            alt="TikTok"
                        />
                    </a>
                    <a
                        class="w-6 h-6 mx-4 md:mx-0 md:my-3"
                        target="_blank"
                        :href="route('external.social_media.instagram')"
                        @click.capture="pushGtmEvent('gtm_click_out_socials')"
                    >
                        <img
                            src="/images/icons/social/icon-instagram.svg"
                            alt="Instagram"
                        />
                    </a>
                    <a
                        class="w-6 h-6 mx-4 md:mx-0 md:my-3"
                        target="_blank"
                        :href="route('external.social_media.linkedin')"
                        @click.capture="pushGtmEvent('gtm_click_out_socials')"
                    >
                        <img
                            src="/images/icons/social/icon-linkedin.svg"
                            alt="LinkedIn"
                        />
                    </a>
                    <a
                        class="w-6 h-6 ml-4 md:ml-0 md:mt-3"
                        target="_blank"
                        :href="route('external.social_media.facebook')"
                        @click.capture="pushGtmEvent('gtm_click_out_socials')"
                    >
                        <img
                            src="/images/icons/social/icon-facebook.svg"
                            alt="Facebook"
                        />
                    </a>
                </div>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
                <div class="flex order-4 w-full mt-15 md:mt-16 lg:mt-0 md:max-w-lg">
                    <div class="w-1/2">
                        <div class="inline-block">
                            <div class="flex flex-col items-center">
                                <a
                                    class="mb-2 md:mb-4"
                                    target="_blank"
                                    :href="route(`external.apps.app_store_${lang}`)"
                                    @click.capture="pushGtmEvent('gtm_click_out_appstores')"
                                >
                                    <img
                                        class="w-32 md:w-40"
                                        :alt="$t('footer.app_badge_text.apple')"
                                        src="/images/badges/apple-app-store-badge.svg"
                                    />
                                </a>
                                <StarRating
                                    class="inline-block"
                                    :star-rating="appRating.apple.score"
                                    :size="starRatingSize"
                                ></StarRating>
                            </div>
                        </div>
                    </div>
                    <div class="w-1/2">
                        <div class="inline-block">
                            <div class="flex flex-col items-center">
                                <a
                                    class="mb-2 md:mb-4"
                                    target="_blank"
                                    :href="route(`external.apps.play_store_${lang}`)"
                                    @click.capture="pushGtmEvent('gtm_click_out_appstores')"
                                >
                                    <img
                                        class="w-32 md:w-40"
                                        :alt="$t('footer.app_badge_text.google')"
                                        src="/images/badges/google-play-store-badge.svg"
                                    />
                                </a>
                                <StarRating
                                    class="inline-block"
                                    :star-rating="appRating.google.score"
                                    :size="starRatingSize"
                                ></StarRating>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="flex flex-col flex-wrap justify-between lg:items-center order-6 w-full mt-15 md:mt-20 lg:mt-32 lg:flex-row"
                >
                    <div class="order-1 text-sm footer-links flex flex-wrap items-center">
                        <a :href="route('landing-page.show', { slug: $t('footer.conditions_slug') })">
                            {{ $t('footer.conditions') }}
                        </a>
                        <a :href="route('landing-page.show', { slug: $t('footer.privacy_slug') })">
                            {{ $t('footer.privacy') }}
                        </a>
                        <a :href="route('landing-page.show', { slug: $t('footer.imprint_slug') })">
                            {{ $t('footer.imprint') }}
                        </a>
                        <a :href="route('about-us')">
                            {{ $t('footer.about_us') }}
                        </a>
                        <a
                            href="#"
                            class="whitespace-nowrap"
                            @click="showPrivacyOptions"
                            >{{ $t('footer.privacy_settings') }}</a
                        >
                        <a :href="route('sitemap.index')">
                            {{ $t('footer.sitemap') }}
                        </a>
                    </div>
                    <LocaleSwitcher class="order-2 pt-2 lg:pt-0 mt-4 lg:mt-0 text-sm lg:order-2 lg:mt-0" />
                    <div class="order-3 mt-4 lg:mt-0 text-sm lg:ml-0">
                        {{ $t('footer.copyright', { year: currentYear }) }}
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>

<script>
import route from '@router/route';
import LocaleSwitcher from '@components/LocaleSwitcher';
import StarRating from '@components/StarRating';
import { mapGetters } from 'vuex';
import { trackClick } from '@/modules/tracking';
import { pushGtmEvent } from '@/utils/gtmUtils';

export default {
    name: 'FooterGuest',
    components: {
        LocaleSwitcher,
        StarRating,
    },
    data() {
        return {
            appRating: {
                apple: {
                    score: 94,
                    reviews: 1715,
                },
                google: {
                    score: 86,
                    reviews: 1715,
                },
            },
        };
    },
    computed: {
        ...mapGetters('ui', ['getLocale', 'screenSize']),
        starRatingSize() {
            return this.screenSize === 'small' ? 'small' : 'normal';
        },
        currentYear() {
            return new Date().getFullYear();
        },
        lang() {
            return this.getLocale.slice(0, 2);
        },
    },
    methods: {
        route,
        pushGtmEvent,
        storeCareerClick(name, value) {
            const clickData = {
                page_field_name: name,
                page_field_value: value,
                page_field_type: 'a',
            };

            trackClick('Career', clickData);
        },
        showPrivacyOptions() {
            // eslint-disable-next-line no-underscore-dangle
            window.__ucCmp?.showSecondLayer();
        },
    },
};
</script>

<style lang="scss" scoped>
.footer {
    .footer-links a:not(:last-child) {
        @apply mr-7;
    }
}

a {
    @apply text-sm leading-6;
}
</style>
